import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import Breadcrumbs from '../../components/ui/breadcrumbs';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import { Settings, Save, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';

const AdminSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    app_name: '',
    app_url: '',
    mail_from_name: '',
    mail_from_address: '',
    openai_api_key: '',
    claude_api_key: '',
    google_client_id: '',
    google_client_secret: '',
    slack_webhook_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/admin/settings');
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put('/admin/settings', settings);
      if (response.data.success) {
        toast.success('Settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearCache = async () => {
    try {
      const response = await axios.post('/admin/settings/cache/clear');
      if (response.data.success) {
        toast.success('Cache cleared successfully');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Failed to clear cache');
    }
  };

  if (!user || !['super_admin', 'admin_super'].includes(user.role)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6"><Breadcrumbs /></div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Settings className="h-8 w-8" />
          Admin Settings
        </h1>
        <p className="text-gray-600">Configure system-wide settings and integrations.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Application Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Application Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="app_name">Application Name</Label>
              <Input
                id="app_name"
                value={settings.app_name}
                onChange={(e) => handleInputChange('app_name', e.target.value)}
                placeholder="LLMChecks"
              />
            </div>
            <div>
              <Label htmlFor="app_url">Application URL</Label>
              <Input
                id="app_url"
                value={settings.app_url}
                onChange={(e) => handleInputChange('app_url', e.target.value)}
                placeholder="https://llmchecks.com"
              />
            </div>
          </CardContent>
        </Card>

        {/* Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Email Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="mail_from_name">From Name</Label>
              <Input
                id="mail_from_name"
                value={settings.mail_from_name}
                onChange={(e) => handleInputChange('mail_from_name', e.target.value)}
                placeholder="LLMChecks"
              />
            </div>
            <div>
              <Label htmlFor="mail_from_address">From Email</Label>
              <Input
                id="mail_from_address"
                type="email"
                value={settings.mail_from_address}
                onChange={(e) => handleInputChange('mail_from_address', e.target.value)}
                placeholder="noreply@llmchecks.com"
              />
            </div>
          </CardContent>
        </Card>

        {/* API Keys */}
        <Card>
          <CardHeader>
            <CardTitle>API Keys</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="openai_api_key">OpenAI API Key</Label>
              <Input
                id="openai_api_key"
                type="password"
                value={settings.openai_api_key}
                onChange={(e) => handleInputChange('openai_api_key', e.target.value)}
                placeholder="sk-..."
              />
            </div>
            <div>
              <Label htmlFor="claude_api_key">Claude API Key</Label>
              <Input
                id="claude_api_key"
                type="password"
                value={settings.claude_api_key}
                onChange={(e) => handleInputChange('claude_api_key', e.target.value)}
                placeholder="sk-ant-..."
              />
            </div>
          </CardContent>
        </Card>

        {/* OAuth Settings */}
        <Card>
          <CardHeader>
            <CardTitle>OAuth Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="google_client_id">Google Client ID</Label>
              <Input
                id="google_client_id"
                value={settings.google_client_id}
                onChange={(e) => handleInputChange('google_client_id', e.target.value)}
                placeholder="Google OAuth Client ID"
              />
            </div>
            <div>
              <Label htmlFor="google_client_secret">Google Client Secret</Label>
              <Input
                id="google_client_secret"
                type="password"
                value={settings.google_client_secret}
                onChange={(e) => handleInputChange('google_client_secret', e.target.value)}
                placeholder="Google OAuth Client Secret"
              />
            </div>
          </CardContent>
        </Card>

        {/* Integrations */}
        <Card>
          <CardHeader>
            <CardTitle>Integrations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="slack_webhook_url">Slack Webhook URL</Label>
              <Input
                id="slack_webhook_url"
                value={settings.slack_webhook_url}
                onChange={(e) => handleInputChange('slack_webhook_url', e.target.value)}
                placeholder="https://hooks.slack.com/..."
              />
            </div>
          </CardContent>
        </Card>

        {/* System Actions */}
        <Card>
          <CardHeader>
            <CardTitle>System Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={clearCache}
              variant="outline"
              className="w-full"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Clear Cache
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="px-8"
        >
          {saving ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
